import { toast } from "react-toastify";
import { TableColumn } from "react-data-table-component";
import { formatTicketNumber } from "./parser";
import { formatDateTime, toTimestamp } from "./date-helper";
import { orderBy } from "lodash";
import { Truck } from "../interface/api/Truck";

// Validate a ticket range for the Generate / Export / Update / Clear tabs.
//
// maxRange is optional: generate and export pass the limit configured in Settings, which
// exists only to catch a mistyped range. Bulk update and clear pass BulkOperationMaxRange,
// which is the old 2,000 cap — those endpoints have not been reworked for large ranges.
export function validateTicketRange(
  fromStr: string,
  toStr: string,
  maxRange?: number
): { from: number; to: number } | null {
  const from = parseInt(fromStr);
  const to = isNaN(parseInt(toStr)) ? from : parseInt(toStr);
  if (isNaN(from) || from > to) {
    toast.error("Invalid ticket range");
    return null;
  }
  if (maxRange && to - from + 1 > maxRange) {
    toast.error(`Maximum ${maxRange.toLocaleString()} tickets at a time`);
    return null;
  }
  return { from, to };
}

// A licence plate is missing when blank (or whitespace) or when the AA99999 mask is only partly
// filled - FormTruckLicensePlateField pads unfilled positions with "_".
export function isLicensePlateMissing(plate: string | null | undefined): boolean {
  return !plate?.trim() || plate.includes("_");
}

// What the hauler + truck # lookup contributes: the registered truck's plate and driver, but
// only into fields still blank - anything already typed on the ticket is kept.
export function fillBlankTruckFields<
  P extends string | null | undefined,
  D extends string | null | undefined
>(
  truck: Truck,
  current: { licensePlate: P; driverName: D }
): { licensePlate: P | string; driverName: D | string } {
  return {
    licensePlate:
      isLicensePlateMissing(current.licensePlate) && truck.licensePlate
        ? truck.licensePlate.toUpperCase()
        : current.licensePlate,
    driverName:
      !current.driverName?.trim() && truck.driverName ? truck.driverName : current.driverName,
  };
}

// Default search payload for ticket search.
// isBSeries (Hydrovac only): undefined = don't filter by batch, false = regular, true = B.
export function createDefaultSearchPayload(
  ticketNumber: string,
  isBSeries?: boolean
) {
  return {
    ticketNumber,
    isBSeries,
    contractorCompanyId: 0,
    transportCompanyId: 0,
    jobSite: "",
    truckLicensePlate: "",
    truckNumber: "",
    fromDate: null,
    toDate: null,
  };
}

// Common column builder: Ticket number column
export function ticketNumberColumn<
  T extends { ticketNumber: number }
>(): TableColumn<T> {
  return {
    name: "#",
    selector: (row) => row.ticketNumber,
    sortable: true,
    width: "80px",
    cell: (row) => formatTicketNumber(row.ticketNumber),
  };
}

// Common column builder: Sale price column
export function salePriceColumn<
  T extends { salePrice: number | null }
>(): TableColumn<T> {
  return {
    name: "Sale Price",
    selector: (row) => row.salePrice ?? "",
    sortable: true,
    width: "100px",
    cell: (row) =>
      row.salePrice != null ? `$${row.salePrice.toFixed(2)}` : "",
  };
}

// Common column builder: a stored time, shown and sorted from the same read so the two
// can't disagree. isZeroTimezone marks a SoilFLO load time, the one time stored as UTC.
export function timeColumn<T>(
  name: string,
  time: (row: T) => Date | string | null | undefined,
  isCompact: boolean,
  isZeroTimezone: (row: T) => boolean = () => false
): TableColumn<T> {
  return {
    name,
    selector: (row) => formatDateTime(time(row), isZeroTimezone(row)),
    sortable: true,
    sortFunction: (a, b) =>
      toTimestamp(time(a), isZeroTimezone(a)) - toTimestamp(time(b), isZeroTimezone(b)),
    width: isCompact ? "130px" : undefined,
    minWidth: isCompact ? undefined : "190px",
    // Wrap the time under the date on narrow screens rather than cutting it off.
    wrap: true,
  };
}

// The Received lists' order: latest receive first, so the last ticket keyed in is easy to
// find and check. Received times are stored local on every stream, SoilFLO loads included.
export function latestReceivedFirst<T>(
  tickets: T[],
  timeReceived: (ticket: T) => Date | string | null | undefined
): T[] {
  return orderBy(tickets, [(t) => toTimestamp(timeReceived(t), false)], ["desc"]);
}
